import { useEffect, useMemo, useState } from "react";
import type { PlatformClient } from "../../platform/supabase";

type Summary={
  caseCount:number;
  ownedCaseCount:number;
  collaboratingCaseCount:number;
  highUrgentCount:number;
  openTaskCount:number;
  overdueReviewCount:number;
  incomingHandoverCount:number;
  nextReviewAt?:string|null;
};

type TeamMetric={
  personId:string;
  displayName:string;
  ownedOpenCases:number;
  collaboratingCases:number;
  highUrgentCases:number;
  openTasks:number;
  overdueReviews:number;
  nextReviewAt?:string|null;
};

type CaseRow={
  caseId:string;
  learnerId:string;
  learnerName:string;
  gradeLevel?:string|null;
  classLabel?:string|null;
  caseType:string;
  title:string;
  summary?:string|null;
  priority:string;
  status:string;
  ownerPersonId:string;
  ownerName:string;
  version:number;
  myRole:string;
  collaboratorCount:number;
  openTaskCount:number;
  noteCount:number;
  currentPlanId?:string|null;
  currentPlanTitle?:string|null;
  currentPlanStatus?:string|null;
  reviewDueAt?:string|null;
  overdueReview:boolean;
  handover?:{
    packageId:string;
    status:string;
    sourcePersonId:string;
    sourceName:string;
    targetPersonId:string;
    targetName:string;
  }|null;
  createdAt:string;
  updatedAt:string;
};

type IncomingHandover={
  packageId:string;
  caseId:string;
  sourcePersonId:string;
  sourceName:string;
  targetPersonId:string;
  targetName:string;
  caseTitle:string;
  learnerId:string;
  learnerName:string;
  transferOpenTasks:boolean;
  retainSourceAsCollaborator:boolean;
  note?:string|null;
  createdAt:string;
};

type CaseloadWorkspace={
  context:{academicYearId?:string|null;canManageSchool:boolean};
  mySummary:Summary;
  team:TeamMetric[];
  cases:CaseRow[];
  incomingHandovers:IncomingHandover[];
};

type Collaborator={
  collaboratorId:string;
  personId:string;
  displayName:string;
  role:string;
  status:string;
  note?:string|null;
  startedAt:string;
  endedAt?:string|null;
};

type Candidate={
  personId:string;
  displayName:string;
  roles:string[];
};

type Note={
  noteId:string;
  noteType:string;
  body:string;
  authorPersonId:string;
  authorName:string;
  createdAt:string;
};

type TeamHandover={
  packageId:string;
  status:string;
  sourcePersonId:string;
  sourceName:string;
  targetPersonId:string;
  targetName:string;
  transferOpenTasks:boolean;
  retainSourceAsCollaborator:boolean;
  note?:string|null;
  decisionNote?:string|null;
  createdAt:string;
  acknowledgedAt?:string|null;
  rejectedAt?:string|null;
  cancelledAt?:string|null;
};

type OpenTask={
  taskId:string;
  title:string;
  status:string;
  priority:string;
  dueAt?:string|null;
  assigneePersonId:string;
  assigneeName:string;
};

type HistoryItem={
  eventId:string;
  eventType?:string;
  fromOwnerName?:string;
  toOwnerName?:string;
  displayName?:string;
  role?:string;
  actorName:string;
  note?:string|null;
  createdAt:string;
};

type CaseTeamWorkspace={
  case:{
    caseId:string;
    learnerId:string;
    learnerName:string;
    caseType:string;
    title:string;
    summary?:string|null;
    priority:string;
    status:string;
    ownerPersonId:string;
    ownerName:string;
    version:number;
  };
  canManageTeam:boolean;
  collaborators:Collaborator[];
  candidates:Candidate[];
  notes:Note[];
  handovers:TeamHandover[];
  openTasks:OpenTask[];
  ownerHistory:HistoryItem[];
  collaborationHistory:HistoryItem[];
};

function fmt(value?:string|null){
  if(!value)return "-";
  return new Date(value).toLocaleString("ko-KR",{
    year:"numeric",month:"2-digit",day:"2-digit",hour:"2-digit",minute:"2-digit"
  });
}

function statusLabel(value:string){
  const map:Record<string,string>={
    OPEN:"열림",ACTIVE:"진행",WAITING:"대기",RESOLVED:"해결",CLOSED:"종료",
    SENT:"전달 대기",ACKNOWLEDGED:"수락",REJECTED:"거절",CANCELLED:"취소",
    DRAFT:"초안",PAUSED:"일시중지",COMPLETED:"완료",
    CO_OWNER:"공동담당",CONTRIBUTOR:"협력",COUNSELOR:"상담",REVIEWER:"검토",CONSULTED:"자문"
  };
  return map[value] || value;
}

function priorityLabel(value:string){
  return value==="URGENT"?"긴급":value==="HIGH"?"우선":value==="LOW"?"낮음":"일반";
}

function myRoleLabel(value:string){
  const map:Record<string,string>={
    OWNER:"담당",COLLABORATOR:"공동담당",INCOMING_HANDOVER:"인수 예정",SCHOOL_ADMIN:"학교관리"
  };
  return map[value] || value;
}

function noteTypeLabel(value:string){
  const map:Record<string,string>={
    UPDATE:"업데이트",DECISION:"결정",QUESTION:"질문",HANDOVER:"인수인계",MEETING:"협의"
  };
  return map[value] || value;
}

function navigate(path:string){
  window.history.pushState({},"",path);
  window.dispatchEvent(new PopStateEvent("popstate"));
}

export function CenterCaseloadPage({
  client,schoolId
}:{
  client:PlatformClient;
  schoolId:string;
}){
  const [data,setData]=useState<CaseloadWorkspace|null>(null);
  const [team,setTeam]=useState<CaseTeamWorkspace|null>(null);
  const [selectedCaseId,setSelectedCaseId]=useState("");
  const [filter,setFilter]=useState<"ALL"|"OWNED"|"COLLAB"|"ATTENTION">("ALL");
  const [query,setQuery]=useState("");
  const [refresh,setRefresh]=useState(0);
  const [busy,setBusy]=useState("");
  const [error,setError]=useState("");
  const [message,setMessage]=useState("");

  const [collabPerson,setCollabPerson]=useState("");
  const [collabRole,setCollabRole]=useState("CONTRIBUTOR");
  const [collabNote,setCollabNote]=useState("");

  const [noteType,setNoteType]=useState("UPDATE");
  const [noteBody,setNoteBody]=useState("");

  const [handoverTarget,setHandoverTarget]=useState("");
  const [handoverNote,setHandoverNote]=useState("");
  const [transferOpenTasks,setTransferOpenTasks]=useState(true);
  const [retainSource,setRetainSource]=useState(false);
  const [decisionNote,setDecisionNote]=useState("");

  useEffect(()=>{
    let active=true;
    setError("");
    client.rpc("center_case_caseload_workspace",{p_school_id:schoolId}).then(({data,error})=>{
      if(!active)return;
      if(error){setError(error.message);return;}
      const next=data as CaseloadWorkspace;
      setData(next);
      const requested=new URLSearchParams(window.location.search).get("case") || "";
      setSelectedCaseId((current)=>
        requested && next.cases.some((item)=>item.caseId===requested)
          ? requested
          : next.cases.some((item)=>item.caseId===current)
            ? current
            : next.cases[0]?.caseId || ""
      );
    });
    return()=>{active=false;};
  },[client,schoolId,refresh]);

  useEffect(()=>{
    if(!selectedCaseId){setTeam(null);return;}
    let active=true;
    client.rpc("center_case_team_workspace",{
      p_school_id:schoolId,p_case_id:selectedCaseId
    }).then(({data,error})=>{
      if(!active)return;
      if(error){setError(error.message);setTeam(null);return;}
      const next=data as CaseTeamWorkspace;
      setTeam(next);
      setCollabPerson((current)=>
        next.candidates.some((item)=>item.personId===current)
          ? current
          : next.candidates[0]?.personId || ""
      );
      const targets=[
        ...next.collaborators.filter((item)=>item.status==="ACTIVE").map((item)=>({personId:item.personId})),
        ...next.candidates
      ].filter((item,index,arr)=>arr.findIndex((x)=>x.personId===item.personId)===index);
      setHandoverTarget((current)=>
        targets.some((item)=>item.personId===current)
          ? current
          : targets[0]?.personId || ""
      );
    });
    return()=>{active=false;};
  },[client,schoolId,selectedCaseId,refresh]);

  async function run(key:string,action:()=>PromiseLike<{data?:unknown;error?:{message:string}|null}>,success?:string){
    setBusy(key);setError("");setMessage("");
    try{
      const result=await action();
      if(result.error)throw new Error(result.error.message);
      if(success)setMessage(success);
      setRefresh((value)=>value+1);
      return result.data;
    }catch(err){
      setError(err instanceof Error?err.message:"처리 중 오류가 발생했습니다.");
      return null;
    }finally{setBusy("");}
  }

  const visibleCases=useMemo(()=>{
    if(!data)return [];
    const q=query.trim().toLowerCase();
    return data.cases.filter((item)=>{
      if(filter==="OWNED" && item.myRole!=="OWNER")return false;
      if(filter==="COLLAB" && item.myRole!=="COLLABORATOR")return false;
      if(filter==="ATTENTION" && !(
        item.priority==="URGENT" || item.priority==="HIGH" || item.overdueReview || item.openTaskCount>0
      ))return false;
      if(!q)return true;
      return [
        item.learnerName,item.gradeLevel,item.classLabel,item.title,item.ownerName,item.caseType
      ].filter(Boolean).join(" ").toLowerCase().includes(q);
    });
  },[data,filter,query]);

  const selectedCase=useMemo(
    ()=>data?.cases.find((item)=>item.caseId===selectedCaseId) || null,
    [data,selectedCaseId]
  );

  const handoverTargets=useMemo(()=>{
    if(!team)return [];
    const map=new Map<string,{personId:string;displayName:string}>();
    team.collaborators.filter((item)=>item.status==="ACTIVE").forEach((item)=>{
      if(item.personId!==team.case.ownerPersonId)map.set(item.personId,{personId:item.personId,displayName:item.displayName});
    });
    team.candidates.forEach((item)=>map.set(item.personId,{personId:item.personId,displayName:item.displayName}));
    return Array.from(map.values());
  },[team]);

  async function addCollaborator(){
    if(!team || !collabPerson)return;
    const result=await run("collab-add",()=>client.rpc("center_add_case_collaborator",{
      p_school_id:schoolId,p_case_id:team.case.caseId,p_person_id:collabPerson,
      p_role:collabRole,p_note:collabNote.trim() || null
    }),"공동담당을 추가했습니다.");
    if(result)setCollabNote("");
  }

  async function endCollaborator(id:string){
    await run("collab-end",()=>client.rpc("center_end_case_collaborator",{
      p_school_id:schoolId,p_collaborator_id:id,p_note:"Case 관리에서 협업 종료"
    }),"공동담당을 종료했습니다.");
  }

  async function addNote(){
    if(!team || !noteBody.trim())return;
    const result=await run("note",()=>client.rpc("center_add_case_note",{
      p_school_id:schoolId,p_case_id:team.case.caseId,
      p_note_type:noteType,p_body:noteBody.trim()
    }),"Case 공유노트를 남겼습니다.");
    if(result)setNoteBody("");
  }

  async function createHandover(){
    if(!team || !handoverTarget)return;
    const result=await run("handover-create",()=>client.rpc("center_create_case_handover",{
      p_school_id:schoolId,p_case_id:team.case.caseId,
      p_target_person_id:handoverTarget,p_transfer_open_tasks:transferOpenTasks,
      p_retain_source_as_collaborator:retainSource,p_note:handoverNote.trim() || null
    }),"Case 인수인계 요청을 보냈습니다.");
    if(result)setHandoverNote("");
  }

  async function decideHandover(packageId:string,decision:"ACCEPT"|"REJECT"|"CANCEL"){
    const success=
      decision==="ACCEPT"?"Case 인수인계를 수락했습니다.":
      decision==="REJECT"?"Case 인수인계를 거절했습니다.":
      "Case 인수인계 요청을 취소했습니다.";
    const result=await run("handover-"+decision,()=>client.rpc("center_decide_case_handover",{
      p_school_id:schoolId,p_package_id:packageId,p_decision:decision,
      p_note:decisionNote.trim() || null
    }),success);
    if(result)setDecisionNote("");
  }

  if(!data)return <section>{error?<p className="error">{error}</p>:<p className="muted">Case 관리 현황을 불러오는 중…</p>}</section>;

  return (
    <section className="erp11-caseload-page">
      <header className="erp11-head">
        <div>
          <p className="eyebrow">Teacher Case Management ERP</p>
          <h2>Case 관리</h2>
          <p>담당 Case의 흐름과 팀 협업, 인수인계를 한곳에서 관리합니다.</p>
        </div>
        <div className="erp11-next-review">
          <span>다음 점검</span>
          <strong>{data.mySummary.nextReviewAt?fmt(data.mySummary.nextReviewAt):"예정 없음"}</strong>
        </div>
      </header>

      {error && <p className="error">{error}</p>}
      {message && <p className="teacher-success">{message}</p>}

      <div className="erp11-metrics">
        <Metric label="내 Case" value={data.mySummary.caseCount}/>
        <Metric label="주담당" value={data.mySummary.ownedCaseCount}/>
        <Metric label="공동담당" value={data.mySummary.collaboratingCaseCount}/>
        <Metric label="우선·긴급" value={data.mySummary.highUrgentCount}/>
        <Metric label="미완료 업무" value={data.mySummary.openTaskCount}/>
        <Metric label="점검 지연" value={data.mySummary.overdueReviewCount}/>
        <Metric label="인수 예정" value={data.mySummary.incomingHandoverCount}/>
      </div>

      {data.incomingHandovers.length>0 && (
        <section className="erp11-incoming">
          <div className="panel-title"><strong>들어온 Case 인수인계</strong><span>{data.incomingHandovers.length}건</span></div>
          {data.incomingHandovers.map((item)=>(
            <article key={item.packageId}>
              <div>
                <span className="erp11-kicker">인수 대기</span>
                <strong>{item.learnerName} · {item.caseTitle}</strong>
                <small>{item.sourceName} → {item.targetName} · {fmt(item.createdAt)}</small>
                {item.note && <p>{item.note}</p>}
                <span className="muted">
                  {item.transferOpenTasks?"미완료 Task 함께 이동":"Task 담당 유지"}
                  {item.retainSourceAsCollaborator?" · 기존 담당자 협력 유지":""}
                </span>
              </div>
              <div className="erp11-decision-actions">
                <input value={decisionNote} onChange={(e)=>setDecisionNote(e.target.value)} placeholder="수락/거절 메모 (선택)"/>
                <button className="primary compact-button" disabled={busy!==""} onClick={()=>decideHandover(item.packageId,"ACCEPT")}>인수 수락</button>
                <button className="secondary" disabled={busy!==""} onClick={()=>decideHandover(item.packageId,"REJECT")}>거절</button>
              </div>
            </article>
          ))}
        </section>
      )}

      {data.context.canManageSchool && data.team.length>0 && (
        <section className="erp11-team-load">
          <div className="panel-title"><strong>학교 Caseload</strong><span>순위가 아니라 현재 업무량과 마감 신호만 봅니다.</span></div>
          <div className="erp11-team-grid">
            {data.team.map((item)=>(
              <article key={item.personId}>
                <strong>{item.displayName}</strong>
                <div><span>주담당</span><b>{item.ownedOpenCases}</b></div>
                <div><span>공동담당</span><b>{item.collaboratingCases}</b></div>
                <div><span>우선·긴급</span><b>{item.highUrgentCases}</b></div>
                <div><span>미완료 Task</span><b>{item.openTasks}</b></div>
                <div><span>점검 지연</span><b>{item.overdueReviews}</b></div>
                <small>다음 점검 {item.nextReviewAt?fmt(item.nextReviewAt):"없음"}</small>
              </article>
            ))}
          </div>
        </section>
      )}

      <div className="erp11-layout">
        <aside className="erp11-case-list-panel">
          <div className="panel-title"><strong>Case 목록</strong><span>{visibleCases.length}/{data.cases.length}건</span></div>
          <input className="teacher-search" value={query} onChange={(e)=>setQuery(e.target.value)} placeholder="학생·Case·담당자 검색"/>
          <div className="erp11-filters">
            {([
              ["ALL","전체"],["OWNED","내 주담당"],["COLLAB","공동담당"],["ATTENTION","확인 필요"]
            ] as const).map(([value,label])=><button key={value} className={filter===value?"active":""} onClick={()=>setFilter(value)}>{label}</button>)}
          </div>
          <div className="erp11-case-list">
            {visibleCases.map((item)=>(
              <button key={item.caseId} className={selectedCaseId===item.caseId?"active":""} onClick={()=>setSelectedCaseId(item.caseId)}>
                <span>
                  <strong>{item.learnerName}</strong>
                  <small>{item.gradeLevel?item.gradeLevel+"학년":""}{item.classLabel?" · "+item.classLabel+"반":""} · {myRoleLabel(item.myRole)}</small>
                  <b>{item.title}</b>
                </span>
                <span className="erp11-case-state">
                  <em>{priorityLabel(item.priority)}</em>
                  <small>{statusLabel(item.status)}</small>
                  {item.overdueReview && <strong>점검 지연</strong>}
                </span>
              </button>
            ))}
            {!visibleCases.length && <p className="case-empty">조건에 맞는 Case가 없습니다.</p>}
          </div>
        </aside>

        <main className="erp11-case-detail">
          {!selectedCase || !team ? (
            <div className="empty-inline large"><strong>Case를 선택하세요.</strong><span>팀 협업과 인수인계 이력을 확인할 수 있습니다.</span></div>
          ) : (
            <>
              <header className="erp11-case-head">
                <div>
                  <p className="eyebrow">{selectedCase.learnerName} · {selectedCase.caseType}</p>
                  <h3>{selectedCase.title}</h3>
                  <p>{selectedCase.summary || "요약 없음"}</p>
                  <div className="erp11-case-chips">
                    <span>{priorityLabel(selectedCase.priority)}</span>
                    <span>{statusLabel(selectedCase.status)}</span>
                    <span>담당 {selectedCase.ownerName}</span>
                    <span>공동 {selectedCase.collaboratorCount}</span>
                    <span>Task {selectedCase.openTaskCount}</span>
                    <span>노트 {selectedCase.noteCount}</span>
                  </div>
                </div>
                <button className="secondary" onClick={()=>navigate("/learners?student="+encodeURIComponent(selectedCase.learnerId))}>Student 360 열기</button>
              </header>

              <div className="erp11-detail-grid">
                <section className="erp11-card">
                  <div className="panel-title"><strong>Case Team</strong><span>{team.collaborators.filter((item)=>item.status==="ACTIVE").length}명 공동담당</span></div>
                  <div className="erp11-collaborators">
                    <div className="erp11-owner"><span><b>주담당</b><strong>{team.case.ownerName}</strong></span><small>Case Owner</small></div>
                    {team.collaborators.filter((item)=>item.status==="ACTIVE").map((item)=>(
                      <div key={item.collaboratorId}>
                        <span><b>{statusLabel(item.role)}</b><strong>{item.displayName}</strong>{item.note && <small>{item.note}</small>}</span>
                        {team.canManageTeam && <button className="text-button" disabled={busy!==""} onClick={()=>endCollaborator(item.collaboratorId)}>협업 종료</button>}
                      </div>
                    ))}
                  </div>
                  {team.canManageTeam && team.candidates.length>0 && (
                    <div className="erp11-add-collab">
                      <select value={collabPerson} onChange={(e)=>setCollabPerson(e.target.value)}>
                        {team.candidates.map((item)=><option key={item.personId} value={item.personId}>{item.displayName}</option>)}
                      </select>
                      <select value={collabRole} onChange={(e)=>setCollabRole(e.target.value)}>
                        <option value="CO_OWNER">공동담당</option>
                        <option value="CONTRIBUTOR">협력</option>
                        <option value="COUNSELOR">상담</option>
                        <option value="REVIEWER">검토</option>
                        <option value="CONSULTED">자문</option>
                      </select>
                      <input value={collabNote} onChange={(e)=>setCollabNote(e.target.value)} placeholder="협업 이유 (선택)"/>
                      <button className="primary compact-button" disabled={busy!=="" || !collabPerson} onClick={addCollaborator}>공동담당 추가</button>
                    </div>
                  )}
                  {team.canManageTeam && !team.candidates.length && <p className="muted">추가 가능한 교사가 없습니다. 공동담당 전 해당 학생 접근권한이 먼저 있어야 합니다.</p>}
                </section>

                <section className="erp11-card">
                  <div className="panel-title"><strong>공유노트</strong><span>append-only</span></div>
                  <div className="erp11-note-compose">
                    <select value={noteType} onChange={(e)=>setNoteType(e.target.value)}>
                      <option value="UPDATE">업데이트</option>
                      <option value="DECISION">결정</option>
                      <option value="QUESTION">질문</option>
                      <option value="MEETING">협의</option>
                      <option value="HANDOVER">인수인계</option>
                    </select>
                    <textarea rows={3} value={noteBody} onChange={(e)=>setNoteBody(e.target.value)} placeholder="Case Team이 함께 볼 내용을 남깁니다."/>
                    <button className="primary compact-button" disabled={busy!=="" || !noteBody.trim()} onClick={addNote}>노트 추가</button>
                  </div>
                  <div className="erp11-note-list">
                    {team.notes.slice(0,12).map((item)=>(
                      <article key={item.noteId}>
                        <header><strong>{noteTypeLabel(item.noteType)}</strong><span>{item.authorName} · {fmt(item.createdAt)}</span></header>
                        <p>{item.body}</p>
                      </article>
                    ))}
                    {!team.notes.length && <p className="case-empty">공유노트가 없습니다.</p>}
                  </div>
                </section>

                <section className="erp11-card">
                  <div className="panel-title"><strong>Case 인수인계</strong><span>접근권한 자동 이전 없음</span></div>
                  {team.canManageTeam && !selectedCase.handover && (
                    <>
                      {handoverTargets.length ? (
                        <div className="erp11-handover-form">
                          <label>새 담당자<select value={handoverTarget} onChange={(e)=>setHandoverTarget(e.target.value)}>{handoverTargets.map((item)=><option key={item.personId} value={item.personId}>{item.displayName}</option>)}</select></label>
                          <label className="check-row"><input type="checkbox" checked={transferOpenTasks} onChange={(e)=>setTransferOpenTasks(e.target.checked)}/> 미완료 Task도 새 담당자에게 이동</label>
                          <label className="check-row"><input type="checkbox" checked={retainSource} onChange={(e)=>setRetainSource(e.target.checked)}/> 기존 담당자를 자문 협력자로 유지</label>
                          <textarea rows={2} value={handoverNote} onChange={(e)=>setHandoverNote(e.target.value)} placeholder="인수인계 메모 (선택)"/>
                          <button className="primary" disabled={busy!=="" || !handoverTarget} onClick={createHandover}>Case 인수인계 요청</button>
                        </div>
                      ) : <p className="muted">인수 가능한 교사가 없습니다. 대상교사에게 학생 접근권한을 먼저 배정하세요.</p>}
                    </>
                  )}

                  {selectedCase.handover && (
                    <div className="erp11-pending-handover">
                      <strong>진행 중</strong>
                      <span>{selectedCase.handover.sourceName} → {selectedCase.handover.targetName}</span>
                      {selectedCase.myRole==="OWNER" && <button className="secondary" disabled={busy!==""} onClick={()=>decideHandover(selectedCase.handover!.packageId,"CANCEL")}>요청 취소</button>}
                    </div>
                  )}

                  <div className="erp11-handover-history">
                    {team.handovers.map((item)=>(
                      <div key={item.packageId}>
                        <span><strong>{item.sourceName} → {item.targetName}</strong><small>{fmt(item.createdAt)}{item.decisionNote?" · "+item.decisionNote:""}</small></span>
                        <b>{statusLabel(item.status)}</b>
                      </div>
                    ))}
                    {!team.handovers.length && <p className="case-empty">Case 인수인계 이력이 없습니다.</p>}
                  </div>
                </section>

                <section className="erp11-card">
                  <div className="panel-title"><strong>미완료 진로업무</strong><span>{team.openTasks.length}건</span></div>
                  <div className="erp11-open-task-list">
                    {team.openTasks.map((item)=>(
                      <div key={item.taskId}>
                        <span><strong>{item.title}</strong><small>{item.assigneeName} · {priorityLabel(item.priority)} · {fmt(item.dueAt)}</small></span>
                        <b>{statusLabel(item.status)}</b>
                      </div>
                    ))}
                    {!team.openTasks.length && <p className="case-empty">미완료 진로업무가 없습니다.</p>}
                  </div>
                </section>

                <details className="erp11-history-card">
                  <summary>팀·소유권 이력</summary>
                  <div className="erp11-timeline">
                    {[
                      ...team.ownerHistory.map((item)=>({...item,label:"소유권",text:(item.fromOwnerName || "")+" → "+(item.toOwnerName || "")})),
                      ...team.collaborationHistory.map((item)=>({...item,label:"협업",text:(item.displayName || "")+" · "+statusLabel(item.eventType || "")+" · "+statusLabel(item.role || "")}))
                    ].sort((a,b)=>new Date(b.createdAt).getTime()-new Date(a.createdAt).getTime()).map((item)=>(
                      <div key={item.eventId}><span><b>{item.label}</b><strong>{item.text}</strong><small>{item.actorName} · {fmt(item.createdAt)}</small></span>{item.note && <p>{item.note}</p>}</div>
                    ))}
                  </div>
                </details>
              </div>
            </>
          )}
        </main>
      </div>
    </section>
  );
}

function Metric({label,value}:{label:string;value:number}){
  return <div className="erp11-metric"><span>{label}</span><strong>{value}</strong></div>;
}
